import { CalendarDays, Clock, MapPin, ExternalLink, Bitcoin } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { CalendarEvent } from '@/hooks/useCalendarEvents';
import { formatEventDate, formatEventTime, isUpcoming } from '@/hooks/useCalendarEvents';
import { nip19 } from 'nostr-tools';

interface MeetupCardProps {
  event: CalendarEvent;
  featured?: boolean;
}

export function MeetupCard({ event, featured = false }: MeetupCardProps) {
  const upcoming = isUpcoming(event);
  const date = formatEventDate(event);
  const time = formatEventTime(event);

  // Build naddr for linking to external Nostr clients
  const naddr = nip19.naddrEncode({
    kind: event.kind,
    pubkey: event.pubkey,
    identifier: event.dTag,
  });

  return (
    <Card
      className={`
        relative overflow-hidden transition-all duration-300 border group
        ${featured
          ? 'border-[#2596be] bg-gradient-to-br from-[#0a1a20] to-[#0d0d0d] shadow-lg shadow-[#2596be]/20'
          : 'border-white/10 bg-[#0a0a0a] hover:border-[#2596be]/50 hover:shadow-md hover:shadow-[#2596be]/10'
        }
      `}
    >
      {/* Top accent bar */}
      <div
        className={`absolute top-0 left-0 right-0 h-0.5 transition-all duration-300
          ${featured ? 'bg-[#2596be]' : 'bg-transparent group-hover:bg-[#2596be]/50'}
        `}
      />

      {/* Event image */}
      {event.image && (
        <div className="relative overflow-hidden h-44 w-full">
          <img
            src={event.image}
            alt={event.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
          {upcoming && (
            <div className="absolute top-3 right-3">
              <Badge className="bg-[#2596be] text-white border-0 shadow-lg shadow-[#2596be]/30 text-xs font-semibold uppercase tracking-wide">
                Upcoming
              </Badge>
            </div>
          )}
        </div>
      )}

      <CardHeader className="pb-2 pt-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className={`font-bold leading-tight text-white ${featured ? 'text-xl' : 'text-lg'}`}>
            {event.title}
          </h3>
          {!event.image && upcoming && (
            <Badge className="shrink-0 bg-[#2596be] text-white border-0 text-xs font-semibold uppercase tracking-wide">
              Upcoming
            </Badge>
          )}
          {!upcoming && (
            <Badge variant="outline" className="shrink-0 border-white/20 text-white/40 text-xs uppercase tracking-wide">
              Past
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="pb-3 space-y-2.5">
        {/* Date */}
        <div className="flex items-center gap-2 text-sm text-white/70">
          <CalendarDays className="h-4 w-4 text-[#2596be] shrink-0" />
          <span>{date}</span>
        </div>

        {/* Time (only for time-based events) */}
        {time && (
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Clock className="h-4 w-4 text-[#2596be] shrink-0" />
            <span>{time}</span>
          </div>
        )}

        {/* Location */}
        {event.location && (
          <div className="flex items-start gap-2 text-sm text-white/70">
            <MapPin className="h-4 w-4 text-[#2596be] shrink-0 mt-0.5" />
            <span className="line-clamp-2">{event.location}</span>
          </div>
        )}

        {/* Bitcoin accepted badge */}
        <div className="flex items-center gap-2 text-xs text-[#2596be]/80 font-medium">
          <Bitcoin className="h-3.5 w-3.5 shrink-0" />
          <span>Bitcoin payments accepted at this venue</span>
        </div>

        {/* Description */}
        {(event.summary || event.description) && (
          <p className="text-sm text-white/60 line-clamp-3 leading-relaxed pt-1">
            {event.summary || event.description}
          </p>
        )}

        {/* Hashtags */}
        {event.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {event.hashtags.slice(0, 4).map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded-full bg-[#2596be]/10 text-[#2596be] border border-[#2596be]/20"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-0 pb-4">
        <Button
          variant="outline"
          size="sm"
          className="w-full border-[#2596be]/30 text-[#2596be] hover:bg-[#2596be] hover:text-white hover:border-[#2596be] transition-all duration-200 gap-2"
          asChild
        >
          <a
            href={`https://plektos.app/event/${naddr}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            View on Plektos
          </a>
        </Button>
      </CardFooter>
    </Card>
  );
}
